import React, { useState } from 'react';
import { compose, withProps } from 'recompose';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { translator } from 'mage-i18n';
import _sumBy from 'lodash/sumBy';
import { SpacerVertical, SpacerHorizontal, Typography } from '@klarna/bubble-ui';
import {
  AddMoreIfAllowed,
  defaultContainerPropTypes,
  HorizontalWrapper,
  moetPropTypes,
  analytics,
} from 'moet-core';
import { generateCyElemId } from 'moet-test-utils/cypress/utils';

import { CAPS } from '@/constants';
import { CY_STAKEHOLDER_CONTAINER_IDS } from '@/constants/stakeholders';
import {
  STAKEHOLDER_ROLES_V2,
  stakeholderUtils,
  withStakeholderLogic,
} from '@/containers/withStakeholderLogic';
import {
  getStakeholders,
  getMerchantAwareTranslator,
  getMerchantCountryKey,
  getMerchantEntityType,
  merchantRegion,
} from '@/selectors/merchant';
import {
  BackButton,
  CallToActionButton,
  ContainerTemplate,
  StakeholderFormCard,
} from '@/components';
import { getFields } from '@/components/PersonForm';
import { prepareStakeholderEventData } from '@/utils/analytics';

import {
  getAreAllTooYoung,
  getSelectableApplicantStakeholders,
  getSubmittedApplicants,
} from './selectors';
import { CY_APPLICANT_CONTAINER_IDS, MIN_AGE, T_PREFIX } from './constants';
import { ANALYTICS_EVENTS } from './analyticsEvents';
import { ApplicantSelector } from './ApplicantSelector';

const { APPLICANT } = STAKEHOLDER_ROLES_V2;
const { BTN_BACK, BTN_SUBMIT } = CY_STAKEHOLDER_CONTAINER_IDS;

export const Applicant = ({
  addedApplicants = [],
  allowToRemove,
  allStakeholders = [],
  areAllTooYoung,
  availableRoles = [],
  error,
  hasBack,
  isDisabled,
  isLoading,
  max,
  merchantCountry,
  merchantEntityType,
  mid,
  onAddStakeholder,
  onBack,
  onBlurStakeholder,
  onChangeStakeholder,
  onNextWrapper,
  onRemoveStakeholder,
  onTouchPersonalId,
  region,
  roleConstraints,
  selectableStakeholders = [],
  stakeholders: applicantStakeholders,
  submittedApplicants = [],
  t,
  tMerchantAware,
}) => {
  const [ctaClicked, setCtaClicked] = useState(false);

  const [selectedApplicantIds, setSelectedApplicantIds] = useState(
    submittedApplicants.map(s => s.id),
  );

  const hasAtLeastOneApplicant = !!selectedApplicantIds.length || !!addedApplicants.length;

  const stakeholdersToAdd = applicantStakeholders
    .map((stakeholder, index) => {
      // Do not filter stakeholders directly because the whole logic is based on stakeholder's
      // index and by filtering stakeholder's index would change.
      if (stakeholder.roles.includes(APPLICANT) && stakeholder.roles.length === 1) {
        // We need the number of stakeholder form fields to calculate the tab indices correctly.
        const fields = Object.keys(
          getFields(stakeholder.data, region, merchantCountry, merchantEntityType),
        );

        // We need to calculate the number of tab indices for this stakeholder's form to be able
        // to set the tab indices of the following elements correctly. That value is the sum of
        // the number of form fields, the number of role toggles and 1 for the "remove" button in
        // case it's being rendered.
        const numTabIndices = fields.length + availableRoles.length + (allowToRemove ? 1 : 0);

        // We need to preserve the original index as stakeholderIndex for our stakeholder logic.
        return { stakeholder, stakeholderIndex: index, numTabIndices };
      }
    })
    .filter(s => !!s);

  // Calculate tabIndex start value for elements being rendered after the stakeholder forms
  const tabIndex = selectableStakeholders.length;
  const tabIndexAfterStakeholders = tabIndex + _sumBy(stakeholdersToAdd, s => s.numTabIndices);

  // For the alert banner that shows when no stakeholder can be selected because all are too young
  const allTooYoungError =
    !stakeholdersToAdd.length && areAllTooYoung
      ? t(`${T_PREFIX}.alert_all_too_young`, { minAge: MIN_AGE })
      : undefined;

  const onChange = selectedApplicantIds => {
    analytics.logEvent(ANALYTICS_EVENTS.selectApplicant, {
      numberOfStakeholders: selectedApplicantIds.length,
    });
    setSelectedApplicantIds(selectedApplicantIds);
  };

  const onSubmit = () => {
    setCtaClicked(true);

    const isDataValid =
      stakeholderUtils.isStakeholdersValid(applicantStakeholders) && hasAtLeastOneApplicant;

    // Map stakeholders' roles to include the Applicant role depending on the selection
    const stakeholdersFromSelector = allStakeholders
      .map(({ roles, ...stakeholder }) => ({
        ...stakeholder,
        roles: selectedApplicantIds.includes(stakeholder.id)
          ? Array.from(new Set([...roles, APPLICANT]))
          : roles.filter(role => role !== APPLICANT),
      }))
      .filter(({ roles }) => roles.length);

    const body = {
      payload: {
        params: { mid },
        body: [
          ...stakeholdersFromSelector,
          ...stakeholderUtils.getStakeholdersMappedValues(addedApplicants),
        ],
      },
    };

    onNextWrapper(body, isDataValid);
  };

  return (
    <ContainerTemplate
      title={tMerchantAware(`${T_PREFIX}.title`)}
      subtitle={tMerchantAware(`${T_PREFIX}.subtitle`)}
      info={tMerchantAware(
        ctaClicked && !hasAtLeastOneApplicant
          ? 'products-fe.error_messages.validation.generic_toggle_undefined'
          : `${T_PREFIX}.robot_message`,
      )}
      error={allTooYoungError || error}
    >
      {selectableStakeholders.length > 0 && (
        <ApplicantSelector
          isDisabled={isDisabled}
          minAge={MIN_AGE}
          stakeholders={selectableStakeholders}
          selectedIds={selectedApplicantIds}
          t={t}
          onChange={onChange}
        />
      )}
      <SpacerVertical small />
      {addedApplicants.length > 0 && (
        <>
          <Typography textToken="text-style/headings/titles/bold/grande">
            {t(`${T_PREFIX}.form_title`)}
          </Typography>
          <SpacerVertical medium />
        </>
      )}
      {stakeholdersToAdd.map(({ stakeholderIndex, stakeholder }, index) => {
        // Calculate tabIndex offset by sum of all previous stakeholders' number of tab indices
        const tabIndexOffset = _sumBy(stakeholdersToAdd.slice(0, index), s => s.numTabIndices);
        return (
          <React.Fragment key={`applicant-${stakeholder.reactFormId}`}>
            <StakeholderFormCard
              autoFocus={index === 0}
              tabIndexStart={tabIndex + tabIndexOffset}
              stakeholder={stakeholder}
              availableRoles={availableRoles}
              disabledRoles={stakeholderUtils.getDisabledRoles(
                roleConstraints,
                applicantStakeholders,
                stakeholder.roles,
              )}
              onBlur={key => onBlurStakeholder(stakeholderIndex, key)}
              onChange={(key, value) => onChangeStakeholder(stakeholderIndex, key, value)}
              onTouchPersonalId={() => onTouchPersonalId(stakeholderIndex)}
              onRemove={allowToRemove ? () => onRemoveStakeholder(stakeholderIndex) : undefined}
              isDisabled={isDisabled}
              name={`applicant-${index}`}
            />
            <SpacerVertical small />
          </React.Fragment>
        );
      })}
      <AddMoreIfAllowed
        tabIndex={tabIndexAfterStakeholders}
        onClick={onAddStakeholder}
        text={t(`${T_PREFIX}.buttons.add_person`)}
        currentCount={applicantStakeholders.length}
        maxCount={max}
        disabled={isDisabled}
        {...generateCyElemId(CY_APPLICANT_CONTAINER_IDS.LINK_ADD_APPLICANT, window)}
      />
      <SpacerVertical large />
      <HorizontalWrapper display="inline-flex" alignItems="center" justifyContent="flex-end">
        {hasBack && (
          <>
            <BackButton
              tabIndex={tabIndexAfterStakeholders + 2}
              onClick={onBack}
              disabled={isDisabled}
              {...generateCyElemId(BTN_BACK, window)}
            >
              {t(`${T_PREFIX}.buttons.back`)}
            </BackButton>
            <SpacerHorizontal medium />
          </>
        )}
        <CallToActionButton
          tabIndex={tabIndexAfterStakeholders + 1}
          onClick={onSubmit}
          loading={isLoading}
          disabled={isDisabled}
          {...generateCyElemId(BTN_SUBMIT, window)}
        >
          {t(`${T_PREFIX}.buttons.next`)}
        </CallToActionButton>
      </HorizontalWrapper>
    </ContainerTemplate>
  );
};

Applicant.propTypes = {
  ...defaultContainerPropTypes,
  ...moetPropTypes.defaultStakeholderContainerPropTypes,
  addedApplicants: PropTypes.array.isRequired,
  allowToRemove: PropTypes.bool.isRequired,
  allStakeholders: PropTypes.array.isRequired, // all listed stakeholders
  areAllTooYoung: PropTypes.bool.isRequired,
  merchantCountry: PropTypes.string.isRequired,
  mid: PropTypes.string.isRequired,
  region: PropTypes.string.isRequired,
  selectableStakeholders: PropTypes.array.isRequired,
  stakeholders: PropTypes.array.isRequired, // applicant stakeholders
  submittedApplicants: PropTypes.array.isRequired,
  t: PropTypes.func.isRequired,
  onBlurStakeholder: PropTypes.func.isRequired,
  onChangeStakeholder: PropTypes.func.isRequired,
};

export default compose(
  connect(state => ({
    allStakeholders: getStakeholders(state),
    areAllTooYoung: getAreAllTooYoung(MIN_AGE)(state),
    merchantCountry: getMerchantCountryKey(state),
    merchantEntityType: getMerchantEntityType(state),
    region: merchantRegion(state),
    selectableStakeholders: getSelectableApplicantStakeholders(MIN_AGE)(state),
    submittedApplicants: getSubmittedApplicants(state),
    t: translator(state),
    tMerchantAware: getMerchantAwareTranslator(state),
  })),
  withProps(({ allStakeholders = [] }) => ({
    currentStakeholdersCount: allStakeholders.length,
  })),
  withStakeholderLogic(CAPS.V2_MERCHANTS_STAKEHOLDERS_PUT, {
    capabilityCallback: ({ payload: { body } = {} }) => {
      if (body?.length) {
        analytics.logEvent(
          ANALYTICS_EVENTS.submitApplicant,
          prepareStakeholderEventData(body, APPLICANT),
        );
      }
    },
  }),
  withProps(({ currentStakeholdersCount, min, stakeholders = [] }) => ({
    addedApplicants: stakeholders.filter(s => s.roles.includes(APPLICANT) && s.roles.length === 1),
    allowToRemove:
      currentStakeholdersCount === 0 ? stakeholders.length > min : stakeholders.length >= min,
  })),
)(Applicant);
